import { countries, ICountry } from 'countries-list';

export interface Country {
  name: string;
  iso2: string;
  dialCode: string;
  flag: string;
}

/**
 * Creates a list of countries with phone codes from countries-list data
 */
export const createCountryList = (): Country[] => {
  const countryData = (Object.entries(countries) as [string, ICountry][])
    .filter(([code, country]) => country.phone.length > 0 && code !== 'UM')
    .map(([code, country]) => ({
      name: country.name,
      iso2: code.toLowerCase(),
      dialCode: `+${country.phone[0]}`,
      flag: code.toLowerCase(),
    }));

  const uniqueCountries = new Map<string, Country>();
  countryData.forEach((country: Country) => {
    const existing = uniqueCountries.get(country.dialCode);
    if (!existing || country.iso2 === 'us') {
      uniqueCountries.set(country.dialCode, country);
    }
  });

  return Array.from(uniqueCountries.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

/**
 * Normalizes a phone value for dial code matching (removes spaces, dashes, parens)
 */
export const normalizePhoneForMatching = (val: string): string =>
  val.replace(/[\s\-().]/g, '');

/**
 * Returns true if we should preserve the current country when the value could
 * still represent it (e.g. "+1" when current is Turks and Caicos +1 649).
 * Prevents reverting to a shorter dial code (US +1) when backspacing.
 */
export const shouldPreserveCountry = (
  normalizedValue: string,
  currentCountry: Country,
): boolean => {
  const currentDialCode = normalizePhoneForMatching(currentCountry.dialCode);
  return (
    normalizedValue.startsWith(currentDialCode) ||
    currentDialCode.startsWith(normalizedValue)
  );
};

/**
 * Finds a country by phone number value (starts with dial code)
 */
export const getCountryForValue = (
  val: string | undefined,
  sortedCountryList: Country[],
): Country | null => {
  if (!val || !val.startsWith('+')) return null;
  const normalized = normalizePhoneForMatching(val);
  return (
    sortedCountryList.find((c) =>
      normalized.startsWith(normalizePhoneForMatching(c.dialCode)),
    ) || null
  );
};

/**
 * Finds a country by ISO code
 */
export const getCountryForCode = (
  code: string | undefined,
  countryList: Country[],
): Country | null => {
  if (!code) return null;
  return countryList.find((c) => c.iso2 === code.toLowerCase()) || null;
};

/**
 * Gets the US country or first country as fallback
 */
export const getDefaultCountry = (countryList: Country[]): Country => {
  return countryList.find((c) => c.iso2 === 'us') || countryList[0];
};

/**
 * Sorts countries by dial code length (longest first) for better matching
 */
export const getSortedCountryList = (countryList: Country[]): Country[] => {
  return [...countryList].sort((a, b) => b.dialCode.length - a.dialCode.length);
};
